const KEY = "bankable_history_v1";
const MAX = 8;

export function getHistory() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    return JSON.parse(raw) || [];
  } catch {
    return [];
  }
}

export function saveToHistory(report) {
  if (!report) return;
  // Never persist sample-mode runs — they'd pollute the returning-user
  // delta view with dummy numbers the user never actually entered.
  if (report.sample_mode) return getHistory();

  const history = getHistory();
  const slim = {
    id: report.id,
    saved_at: new Date().toISOString(),
    company: report.input?.company_name || "Your Business",
    period: report.input?.period || "—",
    industry: report.input?.industry || "—",
    // Legacy flat fields kept for backwards compat with any prior UI.
    score: report.health?.overall_score ?? 0,
    rating: report.health?.rating || "—",
    // Structured objects so PeriodDeltaCard can read `prior.health.*` and
    // `prior.input.*` the same way it reads `current.*`.
    health: {
      overall_score: report.health?.overall_score ?? 0,
      rating: report.health?.rating || "—",
    },
    input: {
      period: report.input?.period || "—",
      currency: report.input?.currency || "USD",
      company_name: report.input?.company_name || "Your Business",
      industry: report.input?.industry || "—",
    },
    metrics: {
      revenue: report.metrics?._aggregates?.revenue ?? 0,
      ebitda: report.metrics?.ebitda ?? 0,
      ebitda_margin: report.metrics?.ebitda_margin ?? 0,
      gross_margin: report.metrics?.gross_margin ?? 0,
      net_margin: report.metrics?.net_margin ?? 0,
      dscr: report.metrics?.dscr ?? 0,
      current_ratio: report.metrics?.current_ratio ?? 0,
      debt_to_ebitda: report.metrics?.debt_to_ebitda ?? 0,
      free_cash_flow:
        report.metrics?.free_cash_flow ??
        report.metrics?._aggregates?.free_cash_flow ??
        null,
      runway_months:
        report.survival_timeline?.runway_months ??
        report.metrics?.runway_months ??
        null,
    },
    // Persisted valuation snapshot — powers the enterprise-value quarter-over-
    // quarter delta on the returning-user report. Uses the backend-computed
    // buyer_view (which already applies quality factor + industry multiple).
    // NOTE: the backend API returns this field as `valuation_range`, not
    // `valuation` — matches the ValuationRangeCard component name.
    valuation: report.valuation_range?.available
      ? {
          low: report.valuation_range.buyer_view?.low ?? 0,
          mid:
            report.valuation_range.buyer_view?.mid ??
            Math.round(
              ((report.valuation_range.buyer_view?.low ?? 0) +
                (report.valuation_range.buyer_view?.high ?? 0)) /
                2
            ),
          high: report.valuation_range.buyer_view?.high ?? 0,
          currency: report.valuation_range.currency || "USD",
        }
      : null,
  };
  // De-dupe by company+period — newer wins
  const filtered = history.filter(
    (h) => !(h.company === slim.company && h.period === slim.period)
  );
  const next = [slim, ...filtered].slice(0, MAX);
  localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export function clearHistory() {
  localStorage.removeItem(KEY);
}

export function removeFromHistory(id) {
  const next = getHistory().filter((h) => h.id !== id);
  localStorage.setItem(KEY, JSON.stringify(next));
  return next;
}
